import { ObjectHelper } from "@bryntum/schedulerpro";

function positionProxy(scheduler, el, fromResource, toResource, positionsMap) {
  if (!positionsMap || !Object.keys(positionsMap).length) return;
  const
    {
      rowHeight,
      resourceStore
    } = scheduler,
    fromIndex = resourceStore.indexOf(fromResource),
    toIndex = resourceStore.indexOf(toResource),
    multiplier = toIndex - fromIndex,
    [left, startTop] = positionsMap[el.dataset.assignmentId],
    top = startTop + multiplier * rowHeight;

  el.style.top = `${top}px`;
  el.style.left = `${left}px`;
}

// This mixin encapsulates copy-down behavior of the scheduler component
export const SchedulerCopyDownMixin = Target => class SchedulerCopyDownMixin extends Target {
  construct(...args) {
    super.construct(...args);

    this.on({
      eventDragStart({ assignmentRecords }) {
        this._originalPositions = {};

        for (const assignmentRecord of assignmentRecords) {
          const el = this.getElementFromAssignmentRecord(assignmentRecord, true);
          this._originalPositions[assignmentRecord.id] = [parseFloat(el.style.left), parseFloat(el.style.top)];
        }
      },

      // This listener is reponsible for putting drag proxies to the view and positioning them
      eventDrag({ assignmentRecords, resourceRecord, newResource, context }) {
        const
          { resourceStore } = this,
          // find start/end resource indices
          fromResourceIndex = resourceStore.indexOf(resourceRecord),
          toResourceIndex = resourceStore.indexOf(newResource),
          proxiesMap = this._myProxyMap = (this._myProxyMap || {}),
          visibleMap = {},
          { left } = context.eventBarEls[0].style;

        if (
          this.features.eventDrag.mode === 'copy' &&
          fromResourceIndex !== toResourceIndex &&
          ![fromResourceIndex, toResourceIndex].includes(-1) &&
          // there should be at least 1 empty row between original row and row the dragged element is
          // currently over
          Math.abs(fromResourceIndex - toResourceIndex) >= 2
        ) {
          const
            // sort them from lower to higher
            // index is a relative
            fromIndex = Math.max(0, Math.min(fromResourceIndex, toResourceIndex)) + 1,
            toIndex = Math.min(resourceStore.count - 1, Math.max(fromResourceIndex, toResourceIndex)) - 1,
            direction = fromResourceIndex > toResourceIndex ? -1 : 1;

          for (const assignmentRecord of assignmentRecords) {
            for (let i = 0; i <= toIndex - fromIndex; i++) {
              const
                { id } = assignmentRecord,
                resourceIndex = (i + 1) * direction + this.resourceStore.indexOf(assignmentRecord.resource);

              let proxy;

              if (id in proxiesMap && resourceIndex in proxiesMap[id]) {
                proxy = proxiesMap[id][resourceIndex];
              }
              else {
                proxy = this.getElementFromAssignmentRecord(assignmentRecord, true);

                if (proxy) {
                  proxy = proxy.cloneNode(true);
                  proxy.classList.remove('b-hidden');
                  proxy.classList.add('b-test-proxy');

                  // Position proxy vertically
                  positionProxy(this, proxy, assignmentRecord.resource, this.resourceStore.getAt(resourceIndex), this._originalPositions);

                  proxy.retainElement = true;

                  proxiesMap[id] = proxiesMap[id] || {};

                  proxiesMap[id][resourceIndex] = proxy;

                  this.foregroundCanvas.appendChild(proxy);
                }
              }

              if (proxy) proxy.style.left = left;

              if (!(id in visibleMap)) {
                visibleMap[id] = {};
              }

              visibleMap[id][resourceIndex] = true;
            }
          }
        }

        Object.entries(proxiesMap).forEach(([assignmentId, proxiesMap]) => {
          Object.entries(proxiesMap).forEach(([index, element]) => {
            if (ObjectHelper.getPath(visibleMap, `${assignmentId}.${index}`)) {
              element.classList.remove('b-hidden');
            }
            else {
              element.classList.add('b-hidden');
            }
          });
        });
      },

      processEventDrop({ isCopy, context }) {
        const {
          startDate,
          endDate,
          assignmentRecords,
          eventRecords,
          browserEvent,
          resourceRecord: fromResource,
          newResource: toResource,
        } = context;

        // In some random cases, isCopy flag is returning as false even though alt key is pressed
        if (!(isCopy || browserEvent.altKey)) {
          return;
        }

        const [eventDragged] = eventRecords;
        const [eventDraggedAssignment] = assignmentRecords;
        const fromResourceIndex = this.resourceStore.indexOf(fromResource);
        const toResourceIndex = this.resourceStore.indexOf(toResource);
        const startIndex = Math.min(fromResourceIndex, toResourceIndex);
        const endIndex = Math.max(fromResourceIndex, toResourceIndex);
        // we could use bryntum's getRange function here but that function does not consider sorting state of records applied on store
        // and always return records from original state.
        const allResources = [];
        for (let i = startIndex + 1; i < endIndex; i++) {
          const resourceRecord = this.resourceStore.getAt(i);
          if (resourceRecord && !resourceRecord.isGroupHeader) {
            allResources.push(resourceRecord);
          }
        }
        const dateRange = { startDate, endDate };
        const eventsToAdd = [], assignmentsToAdd = [];

        for (const resourceRecord of allResources) {
          const newEvent = eventDragged.copy();
          newEvent.startDate = startDate;
          newEvent.endDate = endDate;
          const newAssignment = eventDraggedAssignment.copy();

          newAssignment.event = newEvent;
          newAssignment.resource = resourceRecord;
          if (browserEvent.shiftKey) {
            this.splitEvents({
              dateRange,
              resourceId: resourceRecord.id, // The resource id where the proxy event will be dropped
              canPersist: true // Update the events in the records
            });
          }
          eventsToAdd.push(newEvent);
          assignmentsToAdd.push(newAssignment);
        }
        this.eventStore.add(eventsToAdd);
        this.assignmentStore.add(assignmentsToAdd);
        if (browserEvent.shiftKey) {
          this.splitEvents({
            dateRange,
            resourceId: toResource.id, // The resource id where the proxy event will be dropped
            canPersist: true, // Update the events in the records
          });
        }
      },

      // Cleanup everything on drop
      eventDragReset() {
        Object.values(this._myProxyMap || {}).forEach(map => {
          Object.values(map).forEach(el => el.remove());
        });
        this._myProxyMap = {};
        this._originalPositions = {};
      }
    })
  }
};
